// features/workstation-canvas-panel.js — CANVAS PANEL
// ═══════════════════════════════════════════════════════════
// Phase 2.5: read-only file tile grid backed by workspace_nodes.
// Each file node in the journey map appears as a tile here. Click a
// tile to preview the artifact (image, code, doc, raw text).
//
// Phase 5+ will turn this panel into the real interactive canvas
// (mode registry, content envelope, live edits) per the spec in
// CANVAS_CAPABILITIES.md. Until then this gives the workspace its
// "third panel feels populated" payoff — uploads + Shift-generated
// artifacts both surface here next to the chat and the journey map.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { buildFileLabels, classifyArtifact, downloadArtifact } from '../lib/canvas-classify';
import {
  CanvasPanelHeader,
  StreamingOverlay,
  GalleryBody,
} from '../components/canvas-surface/canvas-gallery';
import { FocusBody } from '../components/canvas-surface/canvas-focus-view';

export default function WorkstationCanvasPanel({
  workspaceGoal,
  workspaceId,
  fileCount,
  fileLimit,
  fileNodes,
  focusedNodeId,
  onFocusNode,
  onClearFocus,
  onCreateBlank,     // (kind: 'doc'|'code'|'html') → creates blank artifact + file node
  onFocusedArtifactChange, // (artifact|null) → notify orchestrator of focused artifact data
  supabase,
  userRecord,
  onFilesDrop,
  onDeleteNode,
}) {
  const [dragOver, setDragOver] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [artifactsById, setArtifactsById] = useState({});
  const [focusFlash, setFocusFlash] = useState(false);
  const [selectedTileIdx, setSelectedTileIdx] = useState(-1);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [buildingArtifact, setBuildingArtifact] = useState(null);
  const [streamingContent, setStreamingContent] = useState(null); // { content, type }
  const [streamingDone, setStreamingDone] = useState(false);
  const [transition, setTransition] = useState(null); // 'focus-in' | 'focus-out' | null
  const panelRef = useRef(null);
  const prevFocusRef = useRef(false);

  // ── Listen for "building" signal from chat fence detection ──
  useEffect(() => {
    function onBuilding(e) {
      const { type, done } = e.detail || {};
      if (done) {
        setBuildingArtifact(null);
      } else if (type) {
        setBuildingArtifact({ type });
        // Clear focus so the streaming overlay can show for the new artifact
        // instead of staying stuck on the old focused artifact
        if (onClearFocus) onClearFocus();
      }
    }
    window.addEventListener('workstation:canvas-building', onBuilding);
    return () => window.removeEventListener('workstation:canvas-building', onBuilding);
  }, []);

  // ── Live streaming: show partial artifact as it's being written ──
  // Never clear streamingContent here — it stays visible as a bridge
  // until Focus Mode activates (cleanup effect below handles that).
  useEffect(() => {
    function onStreaming(e) {
      const { content, type, done } = e.detail || {};
      if (content) setStreamingContent({ content, type: type || 'html' });
      if (done) setStreamingDone(true);
    }
    window.addEventListener('workstation:canvas-streaming', onStreaming);
    return () => window.removeEventListener('workstation:canvas-streaming', onStreaming);
  }, []);

  // ── Focus Mode: resolve focusedNodeId → node + artifact ──
  const focusedNode = focusedNodeId
    ? (fileNodes || []).find(n => n.id === focusedNodeId) || null
    : null;
  const focusedArtifact = focusedNode?.artifact_id
    ? artifactsById[focusedNode.artifact_id] || null
    : null;
  const inFocusMode = !!(focusedNode && focusedArtifact);

  // Clear streaming when Focus Mode activates (artifact saved) — seamless handoff
  useEffect(() => {
    if (inFocusMode) {
      setStreamingContent(null);
      setStreamingDone(false);
      setBuildingArtifact(null);
    }
  }, [inFocusMode]);

  // Notify orchestrator of focused artifact data (for chat context)
  useEffect(() => {
    onFocusedArtifactChange?.(focusedArtifact || null);
  }, [focusedArtifact, onFocusedArtifactChange]);

  // ── Listen for edit-in-place artifact updates from chat panel ──
  useEffect(() => {
    function onArtifactUpdated(e) {
      const { artifactId, content } = e.detail || {};
      if (artifactId && content !== undefined) {
        setArtifactsById(prev => {
          if (!prev[artifactId]) return prev;
          return { ...prev, [artifactId]: { ...prev[artifactId], content } };
        });
      }
    }
    window.addEventListener('workstation:artifact-updated', onArtifactUpdated);
    return () => window.removeEventListener('workstation:artifact-updated', onArtifactUpdated);
  }, []);

  // ── Direct artifact inject — bypass API fetch for new artifacts ──
  // When chat saves a new artifact, it pushes the data directly so
  // the canvas doesn't need a separate /api/user/artifacts fetch.
  useEffect(() => {
    function onArtifactLoaded(e) {
      const { artifact } = e.detail || {};
      if (artifact?.id) {
        console.log('[canvas-panel] artifact injected directly:', artifact.id);
        setArtifactsById(prev => ({ ...prev, [artifact.id]: artifact }));
      }
    }
    window.addEventListener('workstation:artifact-loaded', onArtifactLoaded);
    return () => window.removeEventListener('workstation:artifact-loaded', onArtifactLoaded);
  }, []);

  // ── Transition animation ──
  // Track previous focus state to animate between Focus ↔ Gallery.
  useEffect(() => {
    if (inFocusMode && !prevFocusRef.current) {
      setTransition('focus-in');
      const t = setTimeout(() => setTransition(null), 250);
      return () => clearTimeout(t);
    }
    if (!inFocusMode && prevFocusRef.current) {
      setTransition('focus-out');
      const t = setTimeout(() => setTransition(null), 200);
      return () => clearTimeout(t);
    }
    prevFocusRef.current = inFocusMode;
  }, [inFocusMode]);
  // Keep prevRef in sync (for cases where transition wasn't triggered)
  useEffect(() => { prevFocusRef.current = inFocusMode; });

  // ── Keyboard navigation in Gallery mode ──
  // Arrow keys move between tiles, Enter opens the selected tile.
  // Reset selection when entering Focus Mode or when file list changes.
  useEffect(() => { if (inFocusMode) setSelectedTileIdx(-1); }, [inFocusMode]);

  const handleGalleryKeyDown = useCallback((e) => {
    if (inFocusMode) return;
    const count = (fileNodes || []).length;
    if (count === 0) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedTileIdx(prev => Math.min(prev + 1, count - 1));
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedTileIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && selectedTileIdx >= 0 && selectedTileIdx < count) {
      e.preventDefault();
      const node = fileNodes[selectedTileIdx];
      if (node?.artifact_id) onFocusNode?.(node.id);
    }
  }, [inFocusMode, fileNodes, selectedTileIdx, onFocusNode]);

  // ── ⌘2 focus listener ──
  // Workstation dispatches 'workstation:focus-canvas' on ⌘2. We flash
  // the panel border in gold for a moment so the eye lands on the canvas.
  useEffect(() => {
    function onFocusCanvas() {
      setFocusFlash(true);
      setTimeout(() => setFocusFlash(false), 700);
      panelRef.current?.focus();
      if (!inFocusMode && (fileNodes || []).length > 0 && selectedTileIdx < 0) {
        setSelectedTileIdx(0);
      }
    }
    window.addEventListener('workstation:focus-canvas', onFocusCanvas);
    return () => window.removeEventListener('workstation:focus-canvas', onFocusCanvas);
  }, [inFocusMode, fileNodes, selectedTileIdx]);

  const labels = useMemo(() => buildFileLabels(fileNodes), [fileNodes]);

  // Fetch artifacts for this workspace via the service-role API.
  // The artifacts table has RLS enabled with no SELECT policy, so a
  // browser-client supabase.from('artifacts').select(...) returns zero
  // rows silently. /api/user/artifacts uses the service role key and
  // bypasses RLS — same pattern the rest of the app uses.
  useEffect(() => {
    if (!supabase || !workspaceId) return;
    if (!fileNodes || fileNodes.length === 0) return;
    const missingIds = fileNodes
      .map(n => n.artifact_id)
      .filter(id => id && !artifactsById[id]);
    if (missingIds.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const res = await fetch(`/api/user/artifacts?sessionId=${encodeURIComponent(workspaceId)}`, {
          headers: { 'Authorization': `Bearer ${authSession?.access_token || ''}` },
        });
        if (cancelled) return;
        if (!res.ok) { console.error('[workstation-canvas] artifact fetch failed:', res.status); return; }
        const { artifacts } = await res.json();
        if (cancelled || !artifacts || artifacts.length === 0) return;
        setArtifactsById(prev => {
          const next = { ...prev };
          artifacts.forEach(a => { next[a.id] = a; });
          return next;
        });
      } catch (e) { console.error('[workstation-canvas] artifact fetch failed:', e.message); }
    })();
    return () => { cancelled = true; };
  }, [fileNodes, supabase, workspaceId, artifactsById]);

  function handleDragOver(e) { e.preventDefault(); setDragOver(true); }
  function handleDragLeave(e) { e.preventDefault(); setDragOver(false); }
  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) onFilesDrop?.(files);
  }

  async function handleDelete(node) {
    if (!node || !onDeleteNode) return;
    if (focusedNodeId === node.id) onClearFocus?.();
    await onDeleteNode(node.id);
  }

  async function handleDuplicate() {
    if (!focusedArtifact) return;
    try {
      const { data: { session: authS } } = await supabase.auth.getSession();
      const res = await fetch('/api/user/save-artifact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authS?.access_token || ''}` },
        body: JSON.stringify({
          userId: userRecord?.id,
          sessionId: focusedArtifact.session_id,
          title: `${focusedArtifact.title || 'Untitled'} (copy)`,
          content: focusedArtifact.content,
          artifactType: focusedArtifact.artifact_type,
        }),
      });
      if (res.ok) {
        const { artifact: saved } = await res.json();
        if (saved) window.dispatchEvent(new CustomEvent('workstation:artifact-loaded', { detail: { artifact: saved } }));
      }
    } catch (e) { console.error('[canvas] duplicate failed:', e.message); }
  }

  async function handleRename(newTitle) {
    if (!focusedArtifact) return;
    try {
      const { data: { session: authS } } = await supabase.auth.getSession();
      await fetch('/api/user/artifact-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authS?.access_token || ''}` },
        body: JSON.stringify({ artifactId: focusedArtifact.id, content: focusedArtifact.content, title: newTitle }),
      });
    } catch (e) { console.error('[canvas] rename failed:', e.message); }
  }

  const atLimit = fileCount >= fileLimit;
  const warning = fileCount >= 15 && !atLimit;
  const hasFiles = (fileNodes || []).length > 0;

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      onKeyDown={handleGalleryKeyDown}
      style={{
        flex: 1,
        minWidth: 280,
        background: '#07070a',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        outline: 'none',
        boxShadow: focusFlash ? 'inset 0 0 0 1px #c8a96e, inset 0 0 24px rgba(200,169,110,0.18)' : 'none',
        transition: 'box-shadow 0.3s ease-out',
      }}>
      <style dangerouslySetInnerHTML={{ __html: `
        .ws-canvas-scroll { scrollbar-width: thin; scrollbar-color: #1c1c1c transparent; }
        .ws-canvas-scroll::-webkit-scrollbar { width: 6px; }
        .ws-canvas-scroll::-webkit-scrollbar-track { background: transparent; }
        .ws-canvas-scroll::-webkit-scrollbar-thumb { background: #1e1e2a; border-radius: 3px; }
        .ws-canvas-scroll::-webkit-scrollbar-thumb:hover { background: #2a2a2a; }
      `}} />

      <CanvasPanelHeader
        inFocusMode={inFocusMode}
        focusedNode={focusedNode}
        focusedArtifact={focusedArtifact}
        labels={labels}
        onClearFocus={onClearFocus}
        onDownload={() => downloadArtifact(focusedArtifact, focusedNode)}
        atLimit={atLimit}
        warning={warning}
        fileCount={fileCount}
        fileLimit={fileLimit}
        hasFiles={hasFiles}
        showNewMenu={showNewMenu}
        setShowNewMenu={setShowNewMenu}
        onCreateBlank={onCreateBlank}
        selectMode={selectMode}
        setSelectMode={setSelectMode}
        setSelectedIds={setSelectedIds}
      />

      {/* Live streaming — show Shift building the artifact in real-time */}
      {(streamingContent || buildingArtifact) && !inFocusMode && (
        <StreamingOverlay
          streamingContent={streamingContent}
          streamingDone={streamingDone}
          aiName={userRecord?.ai_name || 'Shift'}
        />
      )}

      {/* Body — Focus Mode OR Gallery Mode */}
      {inFocusMode ? (
        <FocusBody
          focusedNode={focusedNode}
          focusedArtifact={focusedArtifact}
          supabase={supabase}
          userRecord={userRecord}
          transition={transition}
          versionHistoryOpen={versionHistoryOpen}
          setVersionHistoryOpen={setVersionHistoryOpen}
          exportOpen={exportOpen}
          setExportOpen={setExportOpen}
          onDuplicate={handleDuplicate}
          onRename={handleRename}
        />
      ) : (
        <GalleryBody
          dragOver={dragOver}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          transition={transition}
          hasFiles={hasFiles}
          fileNodes={fileNodes}
          labels={labels}
          artifactsById={artifactsById}
          selectedTileIdx={selectedTileIdx}
          selectMode={selectMode}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          onOpenTile={(node) => { if (node.artifact_id) onFocusNode?.(node.id); }}
          onDelete={handleDelete}
          workspaceGoal={workspaceGoal}
          atLimit={atLimit}
          onDownloadSelected={() => {
            selectedIds.forEach(id => {
              const node = (fileNodes || []).find(n => n.id === id);
              const artifact = node?.artifact_id ? artifactsById[node.artifact_id] : null;
              if (artifact && node) downloadArtifact(artifact, node);
            });
          }}
          onDeleteAll={async () => {
            const ids = Array.from(selectedIds);
            for (const id of ids) await onDeleteNode?.(id);
            setSelectedIds(new Set());
            setSelectMode(false);
          }}
        />
      )}
    </div>
  );
}
